import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  shareResource,
  type CreateShareInput,
  type ShareRef,
} from './share.resource';

export const sharesQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['organizations', organizationId, 'shares'],
    queryFn: () => shareResource.list(organizationId),
  });

export function useShares(organizationId: string) {
  return useQuery({
    ...sharesQuery(organizationId),
    select: (data) => data.shares,
  });
}

export const sharedGalleryQuery = (token: string) =>
  queryOptions({
    queryKey: ['shares', token],
    queryFn: () => shareResource.gallery(token),
  });

export function useSharedGallery(token: string) {
  return useQuery(sharedGalleryQuery(token));
}

function useRefresh() {
  const client = useQueryClient();
  return (organizationId: string) =>
    client.invalidateQueries({ queryKey: ['organizations', organizationId, 'shares'] });
}

export function useCreateShare() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: CreateShareInput) => shareResource.create(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useDeleteShare() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (ref: ShareRef) => shareResource.remove(ref),
    onSuccess: (_data, ref) => refresh(ref.organizationId),
  });
}

export const sharedPhotoLoader =
  (token: string) =>
  (photo: { id: string }): Promise<Blob> =>
    shareResource.photo(token, photo.id);

export const sharedThumbnailLoader =
  (token: string) =>
  (photo: { id: string }): Promise<Blob> =>
    shareResource.thumbnail(token, photo.id);
